import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { InferenceAPI, useTokenAndOrgId } from './utils'
import { toast } from 'react-hot-toast'
import {
  ResetOrgUserLimitArgs,
  UpdateOrgLimitReq,
  UpdateOrgUserLimitRequest,
} from './inference.gen'

// ======== useGetOrgSpendingLimit ========
export const useGetOrgSpendingLimit = () => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-spending-limit', token, orgId],
    enabled: !!token && !!orgId,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgSpendingLimit()
      return result.spending_limit
    },
  })
}

// ======== useCreateOrgSpendingLimit ========
export const useCreateOrgSpendingLimit = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { cent: number }) => {
      const result = await InferenceAPI.createOrgSpendingLimit({
        cent: payload.cent,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-spending-limit', token, orgId],
      })

      // Invalidate user balance summary queries to update header
      queryClient.invalidateQueries({
        queryKey: ['org-user-balance-summary'],
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'org-user-balance-summary' &&
          query.queryKey[2] === orgId,
      })

      return result.ok
    },
  })
}

// ======== useCreateOrgUserSpendingLimit ========
export const useCreateOrgUserSpendingLimit = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { cent: number; user_id: number }) => {
      const result = await InferenceAPI.createOrgUserSpendingLimit({
        user_id: payload.user_id,
        cent: payload.cent,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-user-spending-limit', token, orgId, payload.user_id],
      })

      // Invalidate the user balance summary query to update the header
      queryClient.invalidateQueries({
        queryKey: ['org-user-balance-summary', token, orgId, payload.user_id],
      })

      queryClient.invalidateQueries({
        queryKey: ['org-users-spending-limits', token, orgId],
        exact: false,
      })
      toast.success('Successfully created user spending limit')
      return result.ok
    },
  })
}

// ======== useResetOrgUserSpendingLimit ========
export const useResetOrgUserSpendingLimit = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { user_id: number }) => {
      const result = await InferenceAPI.resetOrgUserSpendingLimit({
        user_id: payload.user_id,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-user-spending-limit', token, orgId, payload.user_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['org-users-spending-limits', token, orgId],
        exact: false,
      })
      toast.success('Successfully reset user spending limit')
      return result.ok
    },
  })
}

// ======== useGetOrgUsersSpendingLimits ========
export const useGetOrgUsersSpendingLimit = (page: number) => {
  const { token, orgId } = useTokenAndOrgId()

  const PAGE_SIZE = 10
  return useQuery({
    queryKey: ['org-users-spending-limits', token, orgId, page],
    enabled: !!token && !!orgId,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgUsersSpendingLimit({
        page: page,
        page_size: PAGE_SIZE,
      })
      return {
        spending_limits: result.spending_limits,
        has_more: result.total > PAGE_SIZE,
      }
    },
    placeholderData: keepPreviousData,
  })
}
// ======== useGetOrgUserSpendingLimit ========
export const useGetOrgUserSpendingLimit = (userId: number) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-user-spending-limit', token, orgId, userId],
    enabled: !!token && !!orgId && !!userId,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgUserSpendingLimit({
        user_id: userId,
      })

      return { cent: result.spending_limit.max_spend_cent }
    },
  })
}

// ======== useGetOrgUserLimit ========
export const useGetOrgUserLimit = (userId: number) => {
  const { token, orgId } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['org-user-limit', token, orgId, userId],
    enabled: !!token && !!orgId && !!userId,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgUserLimit({
        user_id: userId,
      })

      return result.limit
    },
  })
}

// ======== useGetOrgUsersLimits ========
export const useGetOrgUsersLimits = (page: number) => {
  const { token, orgId } = useTokenAndOrgId()

  const PAGE_SIZE = 10
  return useQuery({
    queryKey: ['org-users-limits', token, orgId, page],
    enabled: !!token && !!orgId,
    queryFn: async () => {
      const result = await InferenceAPI.getOrgUsersLimit({
        page: page,
        page_size: PAGE_SIZE,
      })
      return {
        limits: result.limits,
        has_more: result.total > PAGE_SIZE,
      }
    },
    placeholderData: keepPreviousData,
  })
}
// ======== useUpdateOrgLimit ========
export const useUpdateOrgLimit = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateOrgLimitReq) => {
      const result = await InferenceAPI.updateOrgLimit({
        request: payload,
      })

      queryClient.invalidateQueries({ queryKey: ['org-limit', token, orgId] })
      queryClient.invalidateQueries({
        queryKey: ['get-org-user-profile'],
        exact: false,
      })

      // Invalidate all org-user-balance-summary queries for this org
      queryClient.invalidateQueries({
        queryKey: ['org-user-balance-summary'],
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'org-user-balance-summary' &&
          query.queryKey[2] === orgId,
      })

      return result.ok
    },
  })
}

// ======== useUpdateOrgUserLimit ========
export const useUpdateOrgUserLimit = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpdateOrgUserLimitRequest) => {
      const result = await InferenceAPI.updateOrgUserLimit({
        request: payload,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-user-limit', token, orgId, payload.user_id],
      })

      // Invalidate user balance summary to update header
      queryClient.invalidateQueries({
        queryKey: ['org-user-balance-summary', token, orgId, payload.user_id],
      })

      toast.success('Successfully updated user limits')
      return result.ok
    },
  })
}

// ======== useResetOrgUserSpendingLimit ========
export const useResetOrgUserLimit = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (args: ResetOrgUserLimitArgs) => {
      const result = await InferenceAPI.resetOrgUserLimit(args)

      queryClient.invalidateQueries({
        queryKey: ['org-user-limit', token, orgId, args.user_id, args.fields],
      })
      queryClient.invalidateQueries({
        queryKey: ['org-users-limits', token, orgId],
        exact: false,
      })

      toast.success("Successfully reset user's limit")
      return result.ok
    },
  })
}

// ======== Spending Limit Automation Hooks ========

// Create spending limit automation
export const useCreateSpendingLimitAutomation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      spending_limit_id: number
      cent: number
      enable: boolean
      max_term?: number
      crontab: string
    }) => {
      const result = await InferenceAPI.createSpendingLimitAutomation({
        req: {
          ...payload,
          max_term: payload.max_term ?? 0, // Use 0 for unlimited when undefined
        },
      })
      
      // Invalidate automations list
      queryClient.invalidateQueries({
        queryKey: ['spending-limit-automations'],
        exact: false,
      })
      
      toast.success('Automation created successfully')
      return result.ok
    },
  })
}

// Delete spending limit automation
export const useDeleteSpendingLimitAutomation = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (autoId: number) => {
      const result = await InferenceAPI.deleteSpendingLimitAutomation({
        auto_id: autoId,
      })
      
      // Invalidate automations list
      queryClient.invalidateQueries({
        queryKey: ['spending-limit-automations', token, orgId],
        exact: false,
      })
      
      toast.success('Automation deleted successfully')
      return result.ok
    },
  })
}

// Get single spending limit automation
export const useGetSpendingLimitAutomation = (autoId: number | null) => {
  return useQuery({
    queryKey: ['spending-limit-automation', autoId],
    enabled: !!autoId,
    queryFn: async () => {
      if (!autoId) return null
      const result = await InferenceAPI.getSpendingLimitAutomation({
        auto_id: autoId,
      })
      return result.data
    },
  })
}

// List spending limit automations
export const useListSpendingLimitAutomations = (spendingLimitId: number | null) => {
  const { token, orgId } = useTokenAndOrgId()
  
  return useQuery({
    queryKey: ['spending-limit-automations', token, orgId, spendingLimitId],
    enabled: !!token && !!orgId && !!spendingLimitId,
    queryFn: async () => {
      if (!spendingLimitId) return []
      const result = await InferenceAPI.listSpendingLimitAutomations({
        spending_limit_id: spendingLimitId,
      })
      return result.automations
    },
  })
}
